import { isUnlimitedTariffLimit } from '@/utils/tariffLimits';

// Lead balance sources, in backend consumption order:
// 1. usable lead bonuses (earliest `start`, then lowest `id`);
// 2. consumable tariffs (earliest `start_at`, then lowest `id`).
// Bonus usage is a separate balance and is never mixed into a tariff's numbers.
//
// The tariff shown as "Current plan" is a separate concept: the most recently
// started active tariff (latest `start_at`, then highest `id`), whether or not
// it still has leads. It never changes which tariff leads are consumed from.

function toTimestamp(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
}

function compareIds(left, right) {
    const a = String(left ?? '');
    const b = String(right ?? '');
    if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
        const x = a.replace(/^0+(?=\d)/, '');
        const y = b.replace(/^0+(?=\d)/, '');
        if (x.length !== y.length) {
            return x.length - y.length;
        }
    }
    return a < b ? -1 : a > b ? 1 : 0;
}

function compareByStartThenId(startKey) {
    return (left, right) => {
        const a = toTimestamp(left?.[startKey]);
        const b = toTimestamp(right?.[startKey]);
        if (a !== b) {
            if (a === null) {
                return 1;
            }
            if (b === null) {
                return -1;
            }
            return a - b;
        }
        return compareIds(left?.id, right?.id);
    };
}

const compareTariffs = compareByStartThenId('start_at');
const compareBonuses = compareByStartThenId('start');

function toLeadCount(value) {
    const count = Number(value);
    return Number.isFinite(count) ? count : 0;
}

function toList(value) {
    return Array.isArray(value) ? value.filter(Boolean) : [];
}

// The tariff's own lead numbers. `null` when its lead limit is unknown.
export function getTariffLeadSummary(tariff) {
    if (!tariff) {
        return null;
    }
    const used = Math.max(0, toLeadCount(tariff.usedLeads));
    if (isUnlimitedTariffLimit(tariff.maxLeadsCount)) {
        return { isUnlimited: true, limit: null, used, remaining: null };
    }
    const limit = Number(tariff.maxLeadsCount);
    if (!Number.isFinite(limit) || limit < 0) {
        return null;
    }
    return { isUnlimited: false, limit, used, remaining: Math.max(0, limit - used) };
}

const DISPLAY_UNLIMITED_LEAD_LIMIT = 100000;

// Display only: the Agency plan and any lead limit above 100,000 are shown as
// unlimited (backend `-1` already is). Never used for consumption or totals.
export function isDisplayUnlimitedTariff(tariff) {
    return tariff?.name === 'Agency' || Number(tariff?.maxLeadsCount) > DISPLAY_UNLIMITED_LEAD_LIMIT;
}

// The tariff's lead numbers as shown in the UI.
export function getTariffDisplayLeadSummary(tariff) {
    if (isDisplayUnlimitedTariff(tariff)) {
        return { isUnlimited: true, limit: null, used: Math.max(0, toLeadCount(tariff.usedLeads)), remaining: null };
    }
    return getTariffLeadSummary(tariff);
}

// Lead limit contract (`maxLeadsCount`): -1 = unlimited, 0 = leads unavailable
// (no lead quota), N > 0 = finite limit. Only an active period with unlimited or
// remaining leads can be consumed; a tariff becomes inactive as soon as its last
// lead is consumed, so an exhausted or zero-limit period never stays current
// even if `is_active` is still true.
export function isConsumableTariff(tariff) {
    if (tariff?.is_active !== true) {
        return false;
    }
    const leads = getTariffLeadSummary(tariff);
    return Boolean(leads && (leads.isUnlimited || leads.remaining > 0));
}

export function getConsumableTariffs(tariffs = []) {
    return toList(tariffs).filter(isConsumableTariff).sort(compareTariffs);
}

// The tariff leads are consumed from first: the oldest consumable one.
export function getConsumedTariff(tariffs = []) {
    return getConsumableTariffs(tariffs)[0] ?? null;
}

// The tariff displayed as "Current plan": the newest `is_active` one. Remaining
// leads and a zero lead limit do not matter here, and the period lifecycle
// (expiry) is the backend's `is_active`. A tariff without `start_at` is only
// displayed when no active tariff has one.
export function getCurrentTariff(tariffs = []) {
    const active = toList(tariffs).filter((tariff) => tariff.is_active === true).sort(compareTariffs);
    const started = active.filter((tariff) => toTimestamp(tariff.start_at) !== null);
    const candidates = started.length ? started : active;
    return candidates[candidates.length - 1] ?? null;
}

// Lead balance of one bonus, or `null` when it gives no usable leads: not
// active, no lead quota (e.g. a feature-only bonus) or exhausted.
// `leads_count = -1` is unlimited. Eligibility (incl. any required tariff) is
// decided by the backend through `active`; never re-check it here.
export function getBonusLeadBalance(bonus) {
    if (bonus?.active !== true) {
        return null;
    }
    const used = Math.max(0, toLeadCount(bonus.used));
    if (isUnlimitedTariffLimit(bonus.leads_count)) {
        return { isUnlimited: true, total: null, used, remaining: null };
    }
    const total = toLeadCount(bonus.leads_count);
    if (total <= 0) {
        return null;
    }
    const remaining = Math.max(0, total - used);
    if (remaining <= 0) {
        return null;
    }
    return { isUnlimited: false, total, used: total - remaining, remaining };
}

export function getUsableLeadBonuses(bonuses = []) {
    return toList(bonuses).filter((bonus) => getBonusLeadBalance(bonus) !== null).sort(compareBonuses);
}

// All usable lead bonuses combined. `total`/`used`/`remaining` cover the finite
// bonuses only; `isUnlimited` is true when any usable bonus is unlimited.
export function getBonusLeadSummary(bonuses = []) {
    const summary = { count: 0, isUnlimited: false, total: 0, used: 0, remaining: 0 };
    for (const bonus of getUsableLeadBonuses(bonuses)) {
        const balance = getBonusLeadBalance(bonus);
        summary.count += 1;
        if (balance.isUnlimited) {
            summary.isUnlimited = true;
            continue;
        }
        summary.total += balance.total;
        summary.used += balance.used;
        summary.remaining += balance.remaining;
    }
    return summary;
}

function getTariffsLeadSummary(tariffs) {
    const summary = { count: 0, isUnlimited: false, remaining: 0 };
    for (const tariff of tariffs) {
        const leads = getTariffLeadSummary(tariff);
        summary.count += 1;
        if (leads?.isUnlimited) {
            summary.isUnlimited = true;
        } else {
            summary.remaining += leads?.remaining ?? 0;
        }
    }
    return summary;
}

// Overall available leads = usable bonus leads + remaining leads of every
// consumable tariff; unlimited when any of those sources is unlimited.
// Available without an active tariff when a usable bonus exists.
// `consumedTariff`/`tariff`/`otherTariffs` describe consumption order;
// `currentTariff`/`currentTariffLeads` describe the displayed "Current plan"
// (its own numbers, possibly 0 left); `nonCurrentTariffs` are the consumable
// tariffs other than it. Current leads + `nonCurrentTariffs` sum to the same
// `tariffRemaining`, since a non-consumable current tariff has 0 left.
export function getLeadOverview({ tarifs = [], bonuses = [] } = {}) {
    const consumableTariffs = getConsumableTariffs(tarifs);
    const [consumedTariff = null, ...nextTariffs] = consumableTariffs;
    const currentTariff = getCurrentTariff(tarifs);
    const tariff = getTariffLeadSummary(consumedTariff);
    const otherTariffs = getTariffsLeadSummary(nextTariffs);
    const bonus = getBonusLeadSummary(bonuses);
    const isUnlimited = bonus.isUnlimited || Boolean(tariff?.isUnlimited) || otherTariffs.isUnlimited;
    const tariffRemaining = tariff?.isUnlimited || otherTariffs.isUnlimited
        ? null
        : (tariff?.remaining ?? 0) + otherTariffs.remaining;

    return {
        isUnlimited,
        consumedTariff,
        tariff,
        otherTariffs,
        currentTariff,
        currentTariffLeads: getTariffLeadSummary(currentTariff),
        nonCurrentTariffs: getTariffsLeadSummary(consumableTariffs.filter((item) => item !== currentTariff)),
        bonus,
        tariffRemaining,
        totalRemaining: isUnlimited ? null : bonus.remaining + tariffRemaining,
    };
}
